import type { Attachment, Communication, Patient } from '@medplum/fhirtypes'
import { useMedplum } from '@medplum/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { logError } from '@/utils/errorUtils'
import { careCoordinationNotesQueryKey } from './useCareCoordinationNotes'
import { buildCommunicationPayload, uploadFiles } from './utils'

type UpdateVars = {
  noteId: string
  note: string
  nextAction: string
  files: File[]
  keptAttachments: Attachment[]
}

export function useUpdateCareCoordinationNote(patient: Patient) {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  return useMutation<Communication, Error, UpdateVars>({
    mutationFn: async ({ noteId, note, nextAction, files, keptAttachments }) => {
      const newAttachments = await uploadFiles(medplum, patient, files)
      const existing = await medplum.readResource('Communication', noteId)
      return medplum.updateResource<Communication>({
        ...existing,
        payload: buildCommunicationPayload(note, nextAction, [
          ...keptAttachments,
          ...newAttachments,
        ]),
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: careCoordinationNotesQueryKey(patient.id!) })
    },
    onError: (e) => {
      logError('patient', 'Failed to update care coordination note', e)
      showNotification({ title: 'Error', message: 'Failed to update note', color: 'red' })
    },
  })
}
